import React, { useState, useEffect, useRef, useCallback } from "react";
import "./styles.less";

interface DropdownProps {
  value?: string;
  options: { label: any; value: string; showCondition?: boolean }[];
  onChange?: Function;
  placeholder: any;
  className?: string;
  optionClassName?: string;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  value,
  options,
  onChange,
  placeholder,
  className,
  optionClassName,
  disabled = false,
}) => {
  const [openOptions, setOpenOptions] = useState<boolean>(false);
  const dropdown = useRef<HTMLDivElement>(null);

  const handleClickLabel = (e: Event | React.BaseSyntheticEvent) => {
    e.stopPropagation();
    toggleOptions();
  };

  const toggleOptions = useCallback(() => {
    setOpenOptions(!openOptions);
  }, [openOptions]);

  const handleChangeOption = (option: { label: string; value: string }) => {
    onChange && onChange(option);
    toggleOptions();
  };

  const selectedValue = () => {
    const selectedOption = options.find((option) => option.value === value);
    return selectedOption ? selectedOption.label : placeholder;
  };

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const outside =
        dropdown.current &&
        !dropdown?.current.contains(e.target as HTMLInputElement);
      outside && openOptions && toggleOptions();
    },
    [openOptions, toggleOptions]
  );

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={`Dropdown ${className} ${disabled && "disabled"}`}
      onClick={handleClickLabel}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleClickLabel(e);
        }
      }}
      ref={dropdown}
    >
      <div className="Dropdown-label">{selectedValue()}</div>
      <div
        className={`Dropdown-options ${optionClassName} ${
          openOptions && "open"
        }`}
      >
        {options.map(
          (option) =>
            (option.showCondition === undefined || option.showCondition) && (
              <button
                type="button"
                key={option.value}
                className="Dropdown-options-item"
                onClick={() => handleChangeOption(option)}
              >
                {option.label}
              </button>
            )
        )}
      </div>
    </div>
  );
};

export default Dropdown;
